import _ from "lodash";
import React from "react";
import {
  CaretDownFilled,
  ChecklistOutlined,
  ClockOutlined,
  NumbersOutlined,
  PaperClipOutlined,
  ScreenRecordOutlined,
  SoupOutlined,
  TextAlignLeftOutlined,
  TextOutlined,
  TwoLinesOutlined,
} from "@toddle-edu/ds-icons";
import { FIELD_TYPE_ENUM } from "./components/CreateRoutineTemplate/CreateRoutineTemplate";

export const removeIdsAndFilterValuesFromFields = fields => {
  return _.map(fields, field => {
    const cleanedField = _.omit(field, "id");

    if (
      _.isEmpty(cleanedField.label) &&
      _.get(cleanedField, "type") === FIELD_TYPE_ENUM.ATTACHMENT
    ) {
      cleanedField.label = "Attachment";
    }

    if (cleanedField.dataSet?.values) {
      cleanedField.dataSet.values = filterAndReorderValues(
        cleanedField.dataSet.values
      );
    }

    if (
      _.isEmpty(cleanedField.label) &&
      _.get(cleanedField, "type") === FIELD_TYPE_ENUM.SECTION_HEADER
    ) {
      cleanedField.label = "Untitled section";
    }
    return cleanedField;
  });
};

export const filterAndReorderValues = values => {
  return _.map(
    _.filter(values, valueItem => !_.isEmpty(valueItem.value)),
    (value, index) => ({
      ...value,
      displaySequence: index + 1,
    })
  );
};
export const formatMappedCourses = courses => {
  return _.map(courses, course => {
    return {
      entityId: _.get(course, "node.id"),
      entityType: "COURSE",
    };
  });
};

export const formatMappedCoursesWithCourseData = entities => {
  return _.map(
    _.filter(entities, entity => _.get(entity, "entityType") === "COURSE"),
    entity => {
      return {
        entityId: _.get(entity, "entity.id"),
        entityType: _.get(entity, "entityType"),
      };
    }
  );
};

export const getCoursesFormatted = entities => {
  return _.map(
    _.filter(entities, entity => _.get(entity, "entityType") === "COURSE"),
    entity => {
      return { node: { ..._.get(entity, "entity") } };
    }
  );
};

export const getCurriculumEntity = entities => {
  return _.map(
    _.filter(
      entities,
      entity => _.get(entity, "entityType") === "CURRICULUM_PROGRAM"
    ),
    entity => {
      return {
        entityId: _.get(entity, "entity.id"),
        entityType: _.get(entity, "entityType"),
      };
    }
  );
};

export const getFieldDropdownOptions = ({ t }) => {
  return [
    {
      key: FIELD_TYPE_ENUM.SECTION_HEADER,
      icon: <TextOutlined />,
      label: t("journal:section_header"),
    },
    {
      key: FIELD_TYPE_ENUM.SHORT_ANSWER,
      icon: <TwoLinesOutlined />,
      label: t("journal:single_line_input"),
    },
    {
      key: FIELD_TYPE_ENUM.PARAGRAPH,
      icon: <TextAlignLeftOutlined />,
      label: t("journal:paragraph_input"),
    },
    {
      key: "1",
      isDivider: true,
    },
    {
      key: FIELD_TYPE_ENUM.SINGLE_SELECT,
      icon: <ScreenRecordOutlined />,
      label: t("journal:single_select"),
    },
    {
      key: FIELD_TYPE_ENUM.MULTI_SELECT,
      icon: <ChecklistOutlined />,
      label: t("journal:multi_select"),
    },
    {
      key: FIELD_TYPE_ENUM.DROPDOWN,
      icon: <CaretDownFilled />,
      label: t("journal:dropdown"),
    },
    {
      key: FIELD_TYPE_ENUM.STEPCOUNT,
      icon: <NumbersOutlined />,
      label: t("journal:stepper_count"),
    },
    {
      key: "2",
      isDivider: true,
    },
    {
      key: FIELD_TYPE_ENUM.TIME,
      icon: <ClockOutlined />,
      label: t("journal:time"),
    },
    {
      key: FIELD_TYPE_ENUM.TIME_RANGE,
      icon: <ClockOutlined />,
      label: t("journal:time_range"),
    },
    {
      key: "3",
      isDivider: true,
    },
    {
      key: FIELD_TYPE_ENUM.MEAL,
      icon: <SoupOutlined />,
      label: t("journal:meals"),
    },
    {
      key: "4",
      isDivider: true,
    },
    {
      key: FIELD_TYPE_ENUM.ATTACHMENT,
      icon: <PaperClipOutlined />,
      label: t("common:attachment"),
    },
  ];
};

export const transformFieldDataSets = templateFields => {
  return _.map(templateFields, field => {
    const dataSets = _.get(field, "dataSets", []);
    const mealDataSet = _.first(
      _.filter(dataSets, dataSet => _.get(dataSet, "type") === "SELECT_OPTION")
    );
    const isMealField = _.get(field, "type") === FIELD_TYPE_ENUM.MEAL;
    let sectionHeaderLabel = _.get(field, "label", "");
    if (
      _.isEmpty(sectionHeaderLabel) &&
      _.get(field, "type") === FIELD_TYPE_ENUM.SECTION_HEADER
    ) {
      sectionHeaderLabel = "Untitled section";
    }
    return {
      ...field,
      label:
        _.get(field, "type") === FIELD_TYPE_ENUM.SECTION_HEADER
          ? sectionHeaderLabel
          : _.get(field, "label", ""),
      dataSet: isMealField
        ? mealDataSet
        : _.size(dataSets) > 0
        ? _.first(dataSets)
        : null,
      ...(isMealField ? { dataSetIds: getMealIds(dataSets) } : {}),
    };
  });
};

export const getMappedMealMenus = templateFields => {
  return _.reduce(
    templateFields,
    (acc, field) => {
      const taggedMenus = getMenusTagged(field);
      if (!_.isEmpty(taggedMenus)) {
        acc[field.id] = taggedMenus;
      }
      return acc;
    },
    {}
  );
};

export const getMenusTagged = field => {
  const dataSets = _.get(field, "dataSets", []);
  const mealMenus = _.filter(
    dataSets,
    dataSet => _.get(dataSet, "type") === "MEAL_MENU"
  );
  return mealMenus;
};

export const getMealIds = dataSets => {
  return _.map(
    _.filter(dataSets, dataSet => _.get(dataSet, "type") === "SELECT_OPTION"),
    dataSetItem => _.get(dataSetItem, "id")
  );
};

export const getMappedClassCount = entities => {
  return _.size(
    _.filter(entities, entity => _.get(entity, "entityType") === "COURSE")
  );
};

export const formatTemplateFieldsUpdateInput = ({
  templateFields,
  oldTemplateFields,
}) => {
  const result = {
    addedFields: [],
    updatedFields: [],
    removedFields: [],
  };

  const oldFieldMap = _.keyBy(oldTemplateFields, "id");
  const newFieldMap = _.keyBy(templateFields, "id");

  _.forEach(newFieldMap, (newField, id) => {
    const oldField = oldFieldMap[id];

    if (newField.dataSet?.values) {
      newField.dataSet.values = filterAndReorderValues(newField.dataSet.values);
    }
    if (oldField?.dataSet?.values) {
      oldField.dataSet.values = filterAndReorderValues(oldField.dataSet.values);
    }

    if (!oldField) {
      result.addedFields.push(_.omit(newField, ["id"]));
    } else {
      const isUpdated = !_.isEqual(newField, oldField);

      if (isUpdated) {
        const updatedField = _.reduce(
          newField,
          (acc, value, key) => {
            if (key !== "id" && !_.isEqual(value, oldField[key])) {
              acc[key] = value;
            }
            return acc;
          },
          { id }
        );

        const oldDataSetIds = _.map(
          _.filter(oldField.dataSets, { type: "MEAL_MENU" }),
          "id"
        );
        const newDataSetIds = _.get(newField, "dataSetIds", []);
        const addedDataSetIds = _.difference(newDataSetIds, oldDataSetIds);
        const removedDataSetIds = _.difference(oldDataSetIds, newDataSetIds);

        if (!_.isEmpty(addedDataSetIds)) {
          updatedField.addedDataSetIds = addedDataSetIds;
        }
        if (!_.isEmpty(removedDataSetIds)) {
          updatedField.removedDataSetIds = removedDataSetIds;
        }
        updatedField.dataSetIds = undefined;

        if (newField.dataSet && oldField.dataSet) {
          const updatedDataSet = {};

          _.forEach(newField.dataSet, (value, key) => {
            if (
              key !== "values" &&
              key !== "id" &&
              !_.isEqual(value, oldField.dataSet[key])
            ) {
              updatedDataSet[key] = value;
            }
          });

          const updatedValues = getUpdatedValues(
            newField.dataSet.values || [],
            oldField.dataSet.values || []
          );

          if (
            updatedValues.addedValues.length > 0 ||
            updatedValues.updatedValues.length > 0 ||
            updatedValues.removedValues.length > 0
          ) {
            updatedDataSet.updateValues = updatedValues;
          }

          if (Object.keys(updatedDataSet).length > 0) {
            updatedField.dataSet = {
              id: newField.dataSet.id,
              ...updatedDataSet,
            };
          }
        }

        if (Object.keys(updatedField).length > 1) {
          result.updatedFields.push(updatedField);
        }
      }
    }
  });

  _.forEach(oldFieldMap, (oldField, id) => {
    if (!newFieldMap[id]) {
      result.removedFields.push(id);
    }
  });

  const isEmptyResult =
    result.addedFields.length === 0 &&
    result.updatedFields.length === 0 &&
    result.removedFields.length === 0;

  return isEmptyResult ? {} : result;
};

const getUpdatedValues = (newValues, oldValues) => {
  const result = {
    addedValues: [],
    updatedValues: [],
    removedValues: [],
  };
  const filteredOldValues = oldValues.filter(val => !_.isEmpty(val));
  const filteredNewValues = newValues.filter(val => !_.isEmpty(val));

  const removedIds = oldValues
    .filter(val => _.isEmpty(val) && val.id)
    .map(val => val.id);

  const oldValuesMap = _.keyBy(filteredOldValues, "id");
  const newValuesMap = _.keyBy(filteredNewValues, "displaySequence");

  _.forEach(newValuesMap, (newValue, displaySequence) => {
    const oldValue = _.find(filteredOldValues, { id: newValue.id });

    if (!oldValue) {
      result.addedValues.push(_.omit(newValue, ["id"]));
    } else if (
      newValue.id &&
      (newValue.value !== oldValue.value ||
        newValue.displaySequence !== oldValue.displaySequence)
    ) {
      const updatedEntry = { id: newValue.id };

      if (newValue.value !== oldValue.value) {
        updatedEntry.value = newValue.value;
      }
      if (newValue.displaySequence !== oldValue.displaySequence) {
        updatedEntry.displaySequence = newValue.displaySequence;
      }

      if (Object.keys(updatedEntry).length > 1) {
        result.updatedValues.push(updatedEntry);
      }
    }
  });

  _.forEach(oldValuesMap, oldValue => {
    if (!_.find(newValuesMap, { id: oldValue.id }) && oldValue.id) {
      result.removedValues.push(oldValue.id);
    }
  });

  result.removedValues.push(...removedIds);

  return result;
};

export const formatMappedEntitiesUpdateInput = ({
  isAllClassesSelected,
  curriculumProgramId,
  newMappedEntities,
  oldMappedEntities,
}) => {
  const result = {
    mappedEntities: {
      addedEntities: [],
      removedEntities: [],
    },
  };

  if (isAllClassesSelected) {
    result.mappedEntities.mapAllCourses = {
      includedCurriculumPrograms: [curriculumProgramId],
    };
    return result;
  }

  const oldEntityMap = _.keyBy(oldMappedEntities, "entityId");
  const newEntityMap = _.keyBy(newMappedEntities, "entityId");
  _.forEach(newEntityMap, (newEntity, entityId) => {
    if (!oldEntityMap[entityId]) {
      result.mappedEntities.addedEntities.push(newEntity);
    }
  });

  _.forEach(oldEntityMap, (oldEntity, entityId) => {
    if (!newEntityMap[entityId]) {
      result.mappedEntities.removedEntities.push(oldEntity);
    }
  });
  return result;
};

export const getCreateTemplateData = ({
  templateTitle,
  curriculumProgramId,
  isAllClassesSelected,
  mappedClasses,
  fields,
}) => {
  return {
    title: !_.isEmpty(templateTitle) ? templateTitle : "Untitled",
    type: "SCHOOL_TEMPLATE",
    state: "PUBLISHED",
    ...(isAllClassesSelected
      ? {
          mapAllCourses: {
            includedCurriculumPrograms: [curriculumProgramId],
          },
        }
      : {}),
    mappedEntities: [
      {
        entityId: curriculumProgramId,
        entityType: "CURRICULUM_PROGRAM",
      },
      ...(!isAllClassesSelected ? formatMappedCourses(mappedClasses) : []),
    ],
    fields: removeIdsAndFilterValuesFromFields(fields),
  };
};

export const getUpdateTemplateData = ({
  templateId,
  curriculumProgramId,
  isAllClassesSelected,
  mappedClasses,
  entityMap,
  templateTitle,
  title,
}) => {
  return {
    id: templateId,
    state: "PUBLISHED",
    ...formatMappedEntitiesUpdateInput({
      curriculumProgramId,
      isAllClassesSelected,
      newMappedEntities: formatMappedCourses(mappedClasses),
      oldMappedEntities: formatMappedCoursesWithCourseData(entityMap),
    }),
    ...(!_.isEqual(templateTitle, title)
      ? { title: !_.isEmpty(templateTitle) ? templateTitle : "Untitled" }
      : {}),
  };
};
